// apps/web/src/pages/author/RecipeFormPanel.tsx
//
// Phase 8 Plan 08 — RecipeFormPanel wires all 6 concrete sections.
//
// PURPOSE: thin scroll-container that hosts all the form sections in the
// SplitPane left panel. Per UI-SPEC §"Spacing — SplitPane left panel"
// line 912: outer padding `p-4` (16px), vertical gap `gap-8` (32px)
// between top-level form sections.
//
// SECTION ORDER per UI-SPEC §"Author UI shell" component-tree (lines
// 116-126):
//   1. Identity
//   2. Ingredients
//   3. Instructions
//   4. Option categories
//   5. Modes
//   6. Widget slots
//   7. Widget instances (Plan 09)
//   8. Constraints
//
// FORMSTATE DISCIPLINE: this component renders sections only. It does NOT
// read useFormState() / formState.* — that discipline is established in
// EditorToolbar.tsx file header.
//
// PHASE 10 (Plan 12) — AI-suggested annotation layer (Surface 3, 10-UI-SPEC // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
// §"Surface 3" lines 196-201). Each section is wrapped in <AISuggestedField
// path={…}> keyed by its top-level RHF path. The wrapper is a PURE NO-OP when
// the path is not in the active session's suggested-paths set (returns the
// children inline with zero DOM), so the Phase 8 panel behavior is preserved
// byte-for-byte when nothing is AI-suggested (T-10-12-05 backward-compat).
//
// Annotation granularity is the SECTION's top-level RHF path (e.g. "title",
// "ingredientGroups", "instructions") — the panel composes whole sections,
// not individual FormFields (those live inside the section components). The
// API's extraction_meta.aiSuggestedPaths therefore carries these top-level
// path tokens; a section is annotated when its path is in the set.

import { AISuggestedField } from "@/components/forms/AISuggestedField";
import { Constraints } from "./sections/Constraints";
import { IdentityFields } from "./sections/IdentityFields";
import { IngredientGroups } from "./sections/IngredientGroups";
import { Instructions } from "./sections/Instructions";
import { Modes } from "./sections/Modes";
import { OptionCategories } from "./sections/OptionCategories";
import { WidgetInstances } from "./sections/WidgetInstances";
import { WidgetSlots } from "./sections/WidgetSlots";

export function RecipeFormPanel(): React.ReactElement {
  return (
    <div className="overflow-auto p-4 flex flex-col gap-8">
      <AISuggestedField path="title">
        <IdentityFields />
      </AISuggestedField>
      <AISuggestedField path="ingredientGroups">
        <IngredientGroups />
      </AISuggestedField>
      <AISuggestedField path="instructions">
        <Instructions />
      </AISuggestedField>
      <AISuggestedField path="optionCategories">
        <OptionCategories />
      </AISuggestedField>
      <AISuggestedField path="modes">
        <Modes />
      </AISuggestedField>
      <AISuggestedField path="widgetSlots">
        <WidgetSlots />
      </AISuggestedField>
      <AISuggestedField path="widgets">
        <WidgetInstances />
      </AISuggestedField>
      <AISuggestedField path="constraints">
        <Constraints />
      </AISuggestedField>
    </div>
  );
}
